import { useShopFlag } from "@/hooks/use-flag";
import { useIsRetractionOnly } from "@/hooks/use-is-retraction-only";
import useTranslation from "@babackapp/translations/use-translation";
import { Badge } from "@babackapp/ui/components/badge";
import {
  Collapsible,
  CollapsibleContent,
} from "@babackapp/ui/components/collapsible";
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@babackapp/ui/components/sidebar";
import { LockClosedIcon } from "@heroicons/react/24/solid";
import { BellIcon } from "@heroicons/react/24/outline";
import { ChevronRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

export function NotificationsNavItem({ shopId }: { shopId: string }) {
  const t = useTranslation();
  const { pathname } = useLocation();
  const isRetractionOnly = useIsRetractionOnly();

  const basePath = `/shops/${shopId}/settings/notifications`;
  const configurationPath = `${basePath}/configuration`;
  const emailTemplatesPath = `${basePath}/email-templates`;
  const workflowsPath = `${basePath}/workflows`;
  const isInNotifications = pathname.startsWith(basePath);
  // Progressive rollout of the return workflows editor (BAB-2854).
  const hasReturnWorkflows = useShopFlag("return-workflows");

  const subItems = [
    {
      label: t("sidebar.notificationsConfiguration"),
      to: configurationPath,
      locked: false,
    },
    {
      label: t("sidebar.emailTemplates"),
      to: emailTemplatesPath,
      locked: isRetractionOnly,
    },
    ...(hasReturnWorkflows
      ? [
          {
            label: t("sidebar.workflows"),
            to: workflowsPath,
            locked: isRetractionOnly,
          },
        ]
      : []),
  ];

  return (
    <Collapsible open={isInNotifications} className="group/notifications">
      <SidebarMenuItem>
        <SidebarMenuButton asChild isActive={isInNotifications}>
          <Link to={configurationPath}>
            <BellIcon className="w-4 h-4" />
            <span>{t("sidebar.notifications")}</span>
            <Badge
              variant="default"
              size="xs"
              className="ml-auto rounded-full border-0 bg-gradient-to-r from-indigo-500 to-purple-500 px-1.5 text-[10px] font-semibold uppercase tracking-wide text-white shadow-sm"
            >
              New
            </Badge>
            <ChevronRight className="h-4 w-4 transition-transform group-data-[state=open]/notifications:rotate-90" />
          </Link>
        </SidebarMenuButton>
        <CollapsibleContent>
          <SidebarMenuSub>
            {subItems.map((item) => (
              <SidebarMenuSubItem key={item.to}>
                <SidebarMenuSubButton
                  asChild
                  isActive={pathname.startsWith(item.to)}
                >
                  <Link
                    to={item.to}
                    className={item.locked ? "opacity-60" : undefined}
                  >
                    <span>{item.label}</span>
                    {item.locked ? (
                      <LockClosedIcon className="ml-auto h-3 w-3 text-muted-foreground" />
                    ) : null}
                  </Link>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}
